import { useEffect, useState, useSyncExternalStore } from 'react';
import { shouldRetryQuery } from '@/lib/queryClient';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCustomer,
  createDriver,
  createLoad,
  createTruck,
  deleteCustomer,
  deleteLoad,
  deleteTruck,
  fetchMapTiles,
  getCustomers,
  getDrivers,
  getGpsDevices,
  getLive,
  getLoad,
  getLoads,
  getTracking,
  getMyLoads,
  getMySettings,
  PLANNER_THEMES,
  getTrucks,
  importTrucks,
  linkTruck,
  markDelivered,
  markPickedUp,
  previewRoute,
  searchPlaces,
  resendInvitation,
  saveMySettings,
  setDriverActive,
  setLoadStatus,
  setTracking,
  syncTruck,
  unlinkTruck,
  updateCustomer,
  updateDriver,
  updateLoad,
  updateTruck,
  type CustomerInput,
  type DriverChangeInput,
  type LoadInput,
  type LoadQuery,
  type LoadStatus,
  type MySettings,
  type PlannerTheme,
  type NewDriverInput,
  type TruckInput,
} from './api';
import { errorCode } from './format';
import { currentSearch } from './picker';
import { allFound, previewOptions, retriesLeft, routePoints, usable, type PlaceContext, type StopDraft } from './stops';

export const fleetKeys = {
  all: ['fleet'] as const,
  trucks: () => [...fleetKeys.all, 'trucks'] as const,
  devices: () => [...fleetKeys.all, 'gps-devices'] as const,
  customers: () => [...fleetKeys.all, 'customers'] as const,
  drivers: () => [...fleetKeys.all, 'drivers'] as const,
  loads: (query: LoadQuery) => [...fleetKeys.all, 'loads', query] as const,
  myLoads: () => [...fleetKeys.all, 'my-loads'] as const,
  live: () => [...fleetKeys.all, 'live'] as const,
  tracking: (id: string) => [...fleetKeys.all, 'tracking', id] as const,
};

/** Asked again every `refetchMs` (Kamioni 15 s, a tura's view 30 s), or only when needed without it */
export const useTrucks = (refetchMs: number | false = false) =>
  useQuery({ queryKey: fleetKeys.trucks(), queryFn: getTrucks, refetchInterval: refetchMs });
/** A GPS failure is not a restart: retrying it only repeats an uncached GPS call */
export const useGpsDevices = () =>
  useQuery({
    queryKey: fleetKeys.devices(),
    queryFn: getGpsDevices,
    retry: (failureCount, error) => !errorCode(error)?.startsWith('gps_') && shouldRetryQuery(failureCount, error),
  });

export const useCustomers = () => useQuery({ queryKey: fleetKeys.customers(), queryFn: getCustomers });
export const useDrivers = () => useQuery({ queryKey: fleetKeys.drivers(), queryFn: getDrivers });
/** The load an answer's link opens; off without an id */
export const useLoad = (id: string | null) =>
  useQuery({ queryKey: [...fleetKeys.all, 'load', id] as const, queryFn: () => getLoad(id!), enabled: id !== null, retry: false });
/** The previous range's loads stay on screen while the next week loads */
export const useLoads = (query: LoadQuery) =>
  useQuery({
    queryKey: fleetKeys.loads(query),
    queryFn: () => getLoads(query),
    placeholderData: keepPreviousData,
    // Drivers change statuses: the planner picks them up within a minute
    refetchInterval: 60_000,
  });
/** Uživo on Početna: the turas going on now, every half minute */
export const useLive = () => useQuery({ queryKey: fleetKeys.live(), queryFn: getLive, refetchInterval: 30_000 });
/** A tura card's tracking and timeline, every half minute while it is open */
export const useTracking = (id: string) => useQuery({ queryKey: fleetKeys.tracking(id), queryFn: () => getTracking(id), refetchInterval: 30_000 });
/** A dispatcher may change the driver's loads at any time: refresh every minute */
export const useMyLoads = () =>
  useQuery({ queryKey: fleetKeys.myLoads(), queryFn: getMyLoads, refetchInterval: 60_000 });

/** Every fleet write refreshes the list and the trackers, whatever the answer */
function useFleetWrite<TArgs, TResult>(write: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSettled: () => {
      // Not returned: the save resolves without waiting for every fleet query (GPS included) to refetch
      void queryClient.invalidateQueries({ queryKey: fleetKeys.all });
    },
  });
}

export const useCreateTruck = () => useFleetWrite((input: TruckInput) => createTruck(input));
export const useUpdateTruck = () =>
  useFleetWrite(({ id, input }: { id: string; input: Partial<TruckInput> }) => updateTruck(id, input));
export const useDeleteTruck = () => useFleetWrite((id: string) => deleteTruck(id));
export const useSyncTruck = () => useFleetWrite((id: string) => syncTruck(id));
export const useLinkTruck = () =>
  useFleetWrite(({ id, deviceId }: { id: string; deviceId: number }) => linkTruck(id, deviceId));
export const useUnlinkTruck = () => useFleetWrite((id: string) => unlinkTruck(id));
export const useImportTrucks = () => useFleetWrite(() => importTrucks());

export const useCreateCustomer = () => useFleetWrite((input: CustomerInput) => createCustomer(input));
export const useUpdateCustomer = () =>
  useFleetWrite(({ id, input }: { id: string; input: Partial<CustomerInput> }) => updateCustomer(id, input));
export const useDeleteCustomer = () => useFleetWrite((id: string) => deleteCustomer(id));
export const useDeleteLoad = () => useFleetWrite((id: string) => deleteLoad(id));
export const useSetTracking = () => useFleetWrite(({ id, enabled }: { id: string; enabled: boolean }) => setTracking(id, enabled));
export const useCreateDriver = () => useFleetWrite((input: NewDriverInput) => createDriver(input));
export const useUpdateDriver = () =>
  useFleetWrite(({ id, input }: { id: string; input: DriverChangeInput }) => updateDriver(id, input));
export const useSetDriverActive = () =>
  useFleetWrite(({ id, active }: { id: string; active: boolean }) => setDriverActive(id, active));
export const useResendInvitation = () =>
  useFleetWrite(({ id, language }: { id: string; language: string }) => resendInvitation(id, language));
export const useCreateLoad = () => useFleetWrite((input: LoadInput) => createLoad(input));
export const useUpdateLoad = () =>
  useFleetWrite(({ id, input }: { id: string; input: Partial<LoadInput> }) => updateLoad(id, input));
export const useSetLoadStatus = () =>
  useFleetWrite(({ id, status }: { id: string; status: LoadStatus }) => setLoadStatus(id, status));
export const useMarkPickedUp = () => useFleetWrite((id: string) => markPickedUp(id));
export const useMarkDelivered = () => useFleetWrite((id: string) => markDelivered(id));

/** The route options for the rows, asked half a second after they stop changing; idle until every stop is found (or stands, by `context`) */
export function useRoutePreview(drafts: StopDraft[], context: PlaceContext = {}) {
  const points = routePoints(drafts);
  const key = JSON.stringify(points.map((point) => [point.place.toLowerCase(), point.lat ?? null, point.lon ?? null]));
  const ready = drafts.length >= 2 && allFound(drafts, context);
  const options = previewOptions();
  const [settled, setSettled] = useState(key);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(key), 500);
    return () => clearTimeout(timer);
  }, [key]);
  return useQuery({
    // Outside fleetKeys.all: saving a load does not need to re-ask ORS
    queryKey: ['fleet-route', settled, options.join(',')],
    queryFn: async () => usable(await previewRoute(points, options)),
    enabled: ready && settled === key,
    staleTime: 60 * 60_000,
    retry: (failureCount, error) => retriesLeft(failureCount) && shouldRetryQuery(failureCount, error),
  });
}

/** Places for what is typed: from 3 characters, 300 ms after typing stops; nothing for older text meanwhile */
export function usePlaceSuggestions(text: string) {
  const query = text.trim();
  const [settled, setSettled] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(query), 300);
    return () => clearTimeout(timer);
  }, [query]);
  const result = useQuery({
    queryKey: ['fleet-places', settled.toLowerCase()],
    queryFn: async () => usable(await searchPlaces(settled)),
    enabled: settled.length >= 3 && settled === query,
    // A place found stays found for the day; "off" is asked again, so search comes back once echo has its key
    staleTime: (cached) => (cached.state.data?.status === 'off' ? 0 : 24 * 60 * 60_000),
    retry: (failureCount, error) => retriesLeft(failureCount) && shouldRetryQuery(failureCount, error),
  });
  return currentSearch(settled, query, result.data, result.isError, result.isFetching);
}

/** Whether place search has answered "off" (echo has no ORS key): typed places then stand as they are */
export function usePlaceSearchOff(): boolean {
  const cache = useQueryClient().getQueryCache();
  return useSyncExternalStore(
    (notify) => cache.subscribe(notify),
    () => cache.findAll({ queryKey: ['fleet-places'] }).some((query) => (query.state.data as { status: string } | undefined)?.status === 'off')
  );
}

/** echo's map tiles, asked again after an hour */
const settingsKey = ['fleet-settings'] as const;
/** The last theme seen on this device: the planner opens in it instead of flashing the default first */
const THEME_KEY = 'fleet.planner.theme';

function lastSettings(): MySettings | undefined {
  try {
    const theme = localStorage.getItem(THEME_KEY);
    return PLANNER_THEMES.includes(theme as PlannerTheme) ? { plannerTheme: theme as PlannerTheme } : undefined;
  } catch {
    return undefined;
  }
}

function rememberSettings(settings: MySettings) {
  try {
    localStorage.setItem(THEME_KEY, settings.plannerTheme);
  } catch {
    // A private window: the planner only flashes the default before the account's settings arrive
  }
}

/** The signed-in user's own settings; only they change them, from the planner */
export const useMySettings = () =>
  useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      const settings = await getMySettings();
      rememberSettings(settings);
      return settings;
    },
    placeholderData: lastSettings,
    staleTime: Infinity,
  });

/** Shown at once; put back when the save fails */
export function useSaveMySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveMySettings,
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: settingsKey });
      const before = queryClient.getQueryData<MySettings>(settingsKey);
      queryClient.setQueryData(settingsKey, next);
      return { before };
    },
    onError: (_error, _next, context) => {
      if (context?.before) queryClient.setQueryData(settingsKey, context.before);
    },
    onSuccess: (saved) => {
      rememberSettings(saved);
      queryClient.setQueryData(settingsKey, saved);
    },
  });
}

export const useMapTiles = () => useQuery({ queryKey: ['fleet-map-tiles'], queryFn: fetchMapTiles, staleTime: 60 * 60_000 });
